import capitalize from 'just-capitalize';
import { z } from 'zod';

import { UserV2 } from '@/services/getUserV2';

const getDates = ({
  startDate,
  endDate,
  isPresent,
}: Pick<
  NonNullable<UserV2['workHistory']>[number],
  'startDate' | 'endDate' | 'isPresent'
>): string => {
  if (isPresent) {
    return startDate ? `${startDate} - Present` : 'Present';
  }

  if (!startDate && !endDate) {
    return '';
  }

  const dates = [startDate, endDate].filter((date) => date && date !== 'null');

  if (dates.length === 0) {
    return '';
  }

  return dates.join(' - ');
};

export const bioSectionItemSchema = z.object({
  title: z.string(),
  text: z.string().nullish(),
});

export type BioSectionItem = z.infer<typeof bioSectionItemSchema>;

const formatSection = <T>(data: T[], formatter: (item: T) => BioSectionItem): BioSectionItem[] =>
  data.map(formatter);

export const bioSectionSchema = z.object({
  title: z.string(),
  data: z.array(bioSectionItemSchema),
});

export type BioSection = z.infer<typeof bioSectionSchema>;

export const mapToBioSections = ({
  workHistory,
  education,
  disclosures,
  about,
}: {
  workHistory: {
    startDate?: string | null;
    endDate?: string | null;
    isPresent?: boolean;
    accolade: string;
  }[];
  education: NonNullable<UserV2['education']>;
  disclosures: NonNullable<UserV2['disclosures']>;
  about?: UserV2['blurb'];
}): BioSection[] => {
  const workHistorySection = formatSection(
    workHistory,
    ({ startDate, endDate, isPresent, accolade }) => ({
      title: getDates({ startDate, endDate, isPresent: !!isPresent }),
      text: accolade,
    })
  );

  const educationSection = formatSection(
    education,
    ({ startDate, endDate, isPresent, degree, institution, studyField }) => {
      const degreeLine = [degree, studyField].filter(Boolean).join(', ');

      const textLines = [degreeLine, institution].filter(Boolean);

      return {
        title: getDates({ startDate, endDate, isPresent: !!isPresent }),
        text: textLines.join('\n'),
      };
    }
  );

  const disclosuresSection = formatSection(disclosures, ({ name, description }) => ({
    title: `${(name || 'nam\ne').split(' ').map(capitalize).join(' ')}`,
    text: description,
  }));

  const sections: BioSection[] = [];

  if (about) {
    sections.push({
      title: 'About Me',
      data: [{ title: '', text: about }],
    });
  }

  if (workHistory?.length > 0) {
    sections.push({ title: 'Work History', data: workHistorySection });
  }

  if (education?.length > 0) {
    sections.push({ title: 'Education & Training', data: educationSection });
  }

  if (disclosures?.length > 0) {
    sections.push({ title: 'Disclosures', data: disclosuresSection });
  }

  return sections;
};
